import { useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolInvoke, toolSuccess, toolError } from '@saas-pages/shared/public/runtime'
import { format, useFormatBytes, useT } from '@/shared/i18n'
import { getUtilityDefinition } from '@/shared/utilities/utilities'
import {
  MAX_FILE_MB,
  validateFileSize,
  convertedFilename,
  convertExcelToPdf,
  classifyXlsx,
  isTooLarge,
  isWideTable,
  ACCEPTED_EXTENSIONS,
  ACCEPT_ATTRIBUTE,
} from './lib'
import { FilePickPrompt } from '@/shared/ui/FilePickPrompt'
import { RevealOnMount } from '@/shared/ui/RevealOnMount'


type Phase =
  | { kind: 'idle' }
  | { kind: 'ready'; file: File }
  | { kind: 'classifying'; file: File }
  | { kind: 'loading'; file: File }
  | { kind: 'success'; file: File; blob: Blob; objectUrl: string; wide: boolean }
  | { kind: 'error'; file: File | null; message: string; errorCode: string }

interface DropZoneProps {
  onFile: (file: File) => void
  disabled: boolean
}

function DropZone({ onFile, disabled }: DropZoneProps) {
  const t = useT()
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return
    const file = files[0]
    if (!file) return
    const ext = '.' + file.name.split('.').pop()?.toLowerCase()
    if (!(ACCEPTED_EXTENSIONS as readonly string[]).includes(ext)) return
    onFile(file)
  }

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={t.common.pickExcel}
      onClick={() => !disabled && inputRef.current?.click()}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          inputRef.current?.click()
        }
      }}
      onDragOver={(e) => {
        e.preventDefault()
        if (!disabled) setDragging(true)
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDragging(false)
        if (!disabled) handleFiles(e.dataTransfer.files)
      }}
      className={cn(
        'flex flex-col items-center justify-center gap-3',
        'rounded-xl border-2 border-dashed p-6 sm:p-10 text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'cursor-pointer select-none transition-colors',
        dragging && !disabled
          ? 'border-primary bg-primary/5'
          : 'border-border hover:border-primary/50 hover:bg-accent/30',
        disabled && 'pointer-events-none opacity-50',
      )}
    >
      {/* Upload icon */}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-primary/60"
        aria-hidden="true"
      >
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
      </svg>

      <div>
        <p className="text-sm font-medium text-foreground">
          <FilePickPrompt drag={t.common.dragExcel} />
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {format(t.common.sizeLimit, { size: MAX_FILE_MB })}
        </p>
        <p className="mt-1 flex items-center justify-center gap-1 text-[11px] text-muted-foreground/80">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          {t.excelVPdf.dropHint}
        </p>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_ATTRIBUTE}
        className="sr-only"
        tabIndex={-1}
        onChange={(e) => handleFiles(e.target.files)}
        disabled={disabled}
      />
    </div>
  )
}


interface FileBadgeProps {
  file: File
  onClear: () => void
  disabled: boolean
}

function FileBadge({ file, onClear, disabled }: FileBadgeProps) {
  const t = useT()
  const formatBytes = useFormatBytes()
  return (
    <div className="flex items-center gap-3 rounded-lg border bg-accent/30 px-4 py-3">
      {/* Document icon */}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="shrink-0 text-primary"
        aria-hidden="true"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
      </svg>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{file.name}</p>
        <p className="text-xs text-muted-foreground">{formatBytes(file.size)}</p>
      </div>

      <button
        type="button"
        onClick={onClear}
        disabled={disabled}
        aria-label={t.common.deleteFile}
        className={cn(
          'shrink-0 rounded p-1 text-muted-foreground transition-colors',
          'hover:bg-destructive/10 hover:text-destructive',
          disabled && 'pointer-events-none opacity-50',
        )}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  )
}


interface ResultPanelProps {
  file: File
  blob: Blob
  objectUrl: string
  wide: boolean
  onReset: () => void
}

function ResultPanel({ file, blob, objectUrl, wide, onReset }: ResultPanelProps) {
  const t = useT()
  const formatBytes = useFormatBytes()

  function handleDownload() {
    const a = document.createElement('a')
    a.href = objectUrl
    a.download = convertedFilename(file.name)
    a.click()
  }

  return (
    <Card className="border-green-200 bg-green-50/50 dark:border-green-500/30 dark:bg-green-500/10">
      <CardContent className="p-5">
        <RevealOnMount />
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {/* Check icon */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0 text-green-600 dark:text-green-300"
            aria-hidden="true"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <span className="text-base font-semibold text-green-700 dark:text-green-200">
            {t.excelVPdf.resultTitle}
          </span>
        </div>

        <div className="mb-4 space-y-1.5 text-sm">
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.common.file}</span>
            <span className="truncate text-right font-medium text-foreground">{file.name}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.common.sourceSize}</span>
            <span className="font-medium text-foreground">{formatBytes(file.size)}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.excelVPdf.pdfLabel}</span>
            <span className="font-medium text-foreground">{formatBytes(blob.size)}</span>
          </div>
        </div>

        {wide && (
          <p className="mb-4 rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100">
            {t.excelVPdf.wideNotice}
          </p>
        )}

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Button onClick={handleDownload} size="lg" className="flex-1">
            {/* Download icon */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            {t.excelVPdf.downloadPdf}
          </Button>
          <Button variant="outline" onClick={onReset} className="flex-1 sm:flex-none">
            {t.excelVPdf.again}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}


interface ErrorPanelProps {
  message: string
  onRetry: () => void
}

function ErrorPanel({ message, onRetry }: ErrorPanelProps) {
  const t = useT()
  return (
    <Card className="border-destructive/30 bg-destructive/5">
      <CardContent className="p-5">
        <div className="mb-3 flex items-center gap-2">
          {/* Alert icon */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0 text-destructive"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span className="text-sm font-semibold text-destructive">{t.common.errorTitle}</span>
        </div>
        <p className="mb-4 text-sm text-foreground">{message}</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          {t.common.retry}
        </Button>
      </CardContent>
    </Card>
  )
}


function Spinner() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="animate-spin"
      aria-hidden="true"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  )
}


const UTILITY = getUtilityDefinition('excelVPdf').analyticsKey

export function ExcelVPdfForm() {
  const t = useT()
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' })
  const abortRef = useRef<AbortController | null>(null)

  const isLoading = phase.kind === 'loading' || phase.kind === 'classifying'
  const hasFile =
    phase.kind === 'ready' ||
    phase.kind === 'loading' ||
    phase.kind === 'classifying' ||
    phase.kind === 'error'
  const currentFile = hasFile ? phase.file : null

  function handleFile(file: File) {
    if (!validateFileSize(file)) {
      toolError(UTILITY, 'client_too_large')
      setPhase({
        kind: 'error',
        file,
        errorCode: 'client_too_large',
        message: format(t.common.tooLarge, { size: MAX_FILE_MB }),
      })
      return
    }

    if (phase.kind === 'success') {
      URL.revokeObjectURL(phase.objectUrl)
    }

    setPhase({ kind: 'ready', file })
  }

  function handleClear() {
    if (phase.kind === 'success') {
      URL.revokeObjectURL(phase.objectUrl)
    }
    abortRef.current?.abort()
    setPhase({ kind: 'idle' })
  }

  function handleRetry() {
    if (phase.kind === 'error' && phase.file) {
      setPhase({ kind: 'ready', file: phase.file })
    } else {
      setPhase({ kind: 'idle' })
    }
  }

  async function handleSubmit() {
    if (phase.kind !== 'ready') return
    const file = phase.file

    const controller = new AbortController()
    abortRef.current = controller

    setPhase({ kind: 'classifying', file })
    toolInvoke(UTILITY)

    try {
      const cls = await classifyXlsx(file)
      if (controller.signal.aborted) return

      if (isTooLarge(cls)) {
        toolError(UTILITY, 'too_many_rows')
        setPhase({
          kind: 'error',
          file,
          errorCode: 'too_many_rows',
          message: t.excelVPdf.tooManyRows,
        })
        return
      }

      setPhase({ kind: 'loading', file })
      const startMs = Date.now()
      const blob = await convertExcelToPdf(file, controller.signal)
      if (controller.signal.aborted) return
      const objectUrl = URL.createObjectURL(blob)

      toolSuccess(UTILITY, Date.now() - startMs, {
        sheets: cls.sheetCount,
        cells: cls.maxCells,
        input_bytes: file.size,
        output_bytes: blob.size,
      })

      setPhase({ kind: 'success', file, blob, objectUrl, wide: isWideTable(cls) })
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') return

      toolError(UTILITY, 'convert_error')
      setPhase({
        kind: 'error',
        file,
        errorCode: 'convert_error',
        message: t.excelVPdf.convertError,
      })
    }
  }


  if (phase.kind === 'success') {
    return (
      <div className="space-y-4">
        <ResultPanel
          file={phase.file}
          blob={phase.blob}
          objectUrl={phase.objectUrl}
          wide={phase.wide}
          onReset={handleClear}
        />
      </div>
    )
  }

  const loadingLabel =
    phase.kind === 'classifying' ? t.excelVPdf.classifying : t.excelVPdf.loadingLocal

  return (
    <div className="space-y-5">
      {currentFile ? (
        <FileBadge file={currentFile} onClear={handleClear} disabled={isLoading} />
      ) : (
        <DropZone onFile={handleFile} disabled={isLoading} />
      )}

      {phase.kind === 'error' && (
        <ErrorPanel message={phase.message} onRetry={handleRetry} />
      )}

      <Button
        size="lg"
        className="w-full"
        disabled={phase.kind !== 'ready' || isLoading}
        onClick={handleSubmit}
      >
        {isLoading ? (
          <>
            <Spinner />
            {loadingLabel}
          </>
        ) : (
          t.excelVPdf.submit
        )}
      </Button>
    </div>
  )
}
